import safeStorage, { safeSessionStorage } from './localStorage';

/**
 * Partnership Referral cookie — carries the partner slug from a co-branded
 * landing page (e.g. /unlimited?partner=greystar) through to the Shopify
 * checkout call. Read server-side at checkout creation to resolve the
 * partner's Shopify discount code (see api-fordays-cloud TRA-224).
 *
 * Cookie is scoped to the .trashie.io apex domain so Webflow and Shopify
 * subdomains can read/write it consistently. Last-touch overwrite: visiting
 * a different /partner_b page updates the cookie; the DB-side row remains
 * locked once the user purchases (first-write-wins).
 */
const COOKIE_KEY = 'partner_ref';
const STORAGE_KEY = 'partner_ref';
const COOKIE_DOMAIN = '.trashie.io';
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

// URL-safe slug: lowercase alphanumeric + hyphen. Rejects anything else so a
// forged cookie value can't smuggle script or unusual characters into the
// checkout payload.
const SLUG_PATTERN = /^[a-z0-9-]+$/;

// Canonicalizes a candidate slug to its lowercase form, or returns null when
// it isn't a valid slug. Lowercasing keeps client-captured values aligned with
// the backend's case-sensitive slug lookup, so ?partner=Greystar and
// ?partner=greystar resolve to the same partner instead of silently missing.
const normalizeSlug = (value) => {
  if (!value) return null;
  const slug = String(value).toLowerCase();
  return SLUG_PATTERN.test(slug) ? slug : null;
};

const readPartnerCookie = () => {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${COOKIE_KEY}=([^;]+)`),
    );
    if (!match) return null;
    return normalizeSlug(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
};

const writePartnerCookie = (slug) => {
  try {
    const val = encodeURIComponent(slug);
    document.cookie = `${COOKIE_KEY}=${val}; domain=${COOKIE_DOMAIN}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  } catch {
    /* silent */
  }
};

/**
 * Returns the stored partner slug (or null).
 * Priority: sessionStorage → cookie → localStorage fallback.
 */
export const getStoredPartnerRef = () => {
  try {
    const ss = normalizeSlug(safeSessionStorage.getItem(STORAGE_KEY));
    if (ss) return ss;

    const cookieVal = readPartnerCookie();
    if (cookieVal) {
      safeSessionStorage.setItem(STORAGE_KEY, cookieVal);
      return cookieVal;
    }

    const ls = normalizeSlug(safeStorage.getItem(STORAGE_KEY));
    if (ls) {
      safeSessionStorage.setItem(STORAGE_KEY, ls);
      return ls;
    }

    return null;
  } catch {
    return null;
  }
};

export const setPartnerRef = (rawSlug) => {
  const slug = normalizeSlug(rawSlug);
  if (!slug) return;
  try {
    writePartnerCookie(slug);
    safeStorage.setItem(STORAGE_KEY, slug);
    safeSessionStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* silent */
  }
};

/**
 * Captures ?partner=<slug> from the current URL and stores it (cookie +
 * storage). Last-touch overwrite — visiting a different partner page
 * updates the stored value. Returns the resulting stored slug, falling
 * back to whatever was already stored when the URL has no param.
 *
 * Intended to be called once on /unlimited mount.
 */
export const capturePartnerRefFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const slug = normalizeSlug(params.get('partner'));
    if (slug) {
      setPartnerRef(slug);
      return slug;
    }
  } catch {
    /* silent */
  }
  return getStoredPartnerRef();
};
